import React, { useState } from "react";
import Navbar from "../Navbar/Navbar";
import { EnvelopeIcon, MapPinIcon, PhoneIcon } from "@heroicons/react/24/outline";

export default function HeroContact() {
  const [formData, setFormData] = useState({
    fullname: "",
    company: "",
    message: "",
    budget: "",
    email: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(
      `New enquiry from ${formData.fullname}${formData.company ? ` (${formData.company})` : ""}`
    );
    const body = encodeURIComponent(
      `Name: ${formData.fullname}\nEmail: ${formData.email}\nCompany: ${formData.company || "N/A"}\nBudget: ${formData.budget || "N/A"}\n\nMessage:\n${formData.message}`
    );
    window.location.href = `mailto:devdesk.services@gmail.com?subject=${subject}&body=${body}`;
    setSubmitted(true);
    setFormData({ fullname: "", company: "", message: "", budget: "", email: "" });
    setTimeout(() => setSubmitted(false), 5000);
  };

  return (
    <>
      <Navbar />

      <section className="relative pt-32 pb-24 lg:pt-40 bg-white overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#f0f0f0_1px,transparent_1px),linear-gradient(to_bottom,#f0f0f0_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)]" />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Left */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-full mb-8 border border-blue-100">
                <div className="w-1.5 h-1.5 rounded-full bg-[#0186ef]" />
                <span className="text-xs font-semibold text-[#0186ef] uppercase tracking-wider">Get in Touch</span>
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 leading-[1.1] tracking-tight mb-6">
                Let's collaborate on your next 
                <span className="text-[#0186ef]"> big idea</span>
              </h1>

              <p className="text-lg text-gray-500 leading-relaxed mb-12 max-w-md">
                Reach out to discuss your business needs or just to say hello. 
                We'd love to hear from you.
              </p>

              {/* Contact info */}
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#0186ef]/10 flex items-center justify-center flex-shrink-0">
                    <EnvelopeIcon className="w-5 h-5 text-[#0186ef]" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Email</div>
                    <a href="mailto:devdesk.services@gmail.com" className="font-semibold text-gray-900 hover:text-[#0186ef] transition-colors">
                      devdesk.services@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#0186ef]/10 flex items-center justify-center flex-shrink-0">
                    <PhoneIcon className="w-5 h-5 text-[#0186ef]" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Phone</div>
                    <a href="tel:08020595269" className="font-semibold text-gray-900 hover:text-[#0186ef] transition-colors">
                      +234 802 059 5269
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#0186ef]/10 flex items-center justify-center flex-shrink-0">
                    <MapPinIcon className="w-5 h-5 text-[#0186ef]" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Location</div>
                    <div className="font-semibold text-gray-900">Nigeria</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Form */}
            <div className="bg-gray-50 rounded-3xl p-8 lg:p-10 border border-gray-100">
              {submitted ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-16">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                    <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank you!</h3>
                  <p className="text-gray-500">Your email client should have opened. We'll be in touch soon.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="fullname" className="block text-sm font-semibold text-gray-900 mb-2">
                      Your Name
                    </label>
                    <input
                      id="fullname"
                      name="fullname"
                      type="text"
                      placeholder="John Doe"
                      value={formData.fullname}
                      onChange={handleChange}
                      required
                      className="block w-full rounded-xl border-0 px-5 py-3.5 text-gray-900 bg-white shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-[#0186ef] text-sm transition-shadow"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                      Email Address
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@company.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="block w-full rounded-xl border-0 px-5 py-3.5 text-gray-900 bg-white shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-[#0186ef] text-sm transition-shadow"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-gray-900 mb-2">
                      Company
                    </label>
                    <input
                      id="company"
                      name="company"
                      type="text"
                      placeholder="Your company name"
                      value={formData.company}
                      onChange={handleChange}
                      className="block w-full rounded-xl border-0 px-5 py-3.5 text-gray-900 bg-white shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-[#0186ef] text-sm transition-shadow"
                    />
                  </div>

                  <div>
                    <label htmlFor="budget" className="block text-sm font-semibold text-gray-900 mb-2">
                      Budget Range
                    </label>
                    <input
                      id="budget"
                      name="budget"
                      type="text"
                      placeholder="e.g., $5,000 - $10,000"
                      value={formData.budget}
                      onChange={handleChange}
                      className="block w-full rounded-xl border-0 px-5 py-3.5 text-gray-900 bg-white shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-[#0186ef] text-sm transition-shadow"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                      How can we help?
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      placeholder="Tell us about your project..."
                      value={formData.message}
                      onChange={handleChange}
                      required
                      className="block w-full rounded-xl border-0 px-5 py-3.5 text-gray-900 bg-white shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-[#0186ef] text-sm resize-none transition-shadow"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3.5 px-6 text-sm font-semibold text-white bg-[#0186ef] rounded-xl hover:bg-[#0070d0] transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/25"
                  >
                    Send Message
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
